import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import QRCode from "qrcode";
import api, { formatApiError } from "@/lib/api";
import { guestApi } from "@/lib/guest";
import { money, asset } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Monitor, Plus, Minus, Loader2, Search, Leaf, Beef, ShoppingBag, UtensilsCrossed } from "lucide-react";

function itemPrice(m, mode) {
  if (mode === "takeaway" && Number(m.price_takeaway) > 0) return Number(m.price_takeaway);
  return Number(m.price) || 0;
}

export default function Kiosk() {
  const { slug } = useParams();
  const [mode, setMode] = useState("takeaway");
  const [cart, setCart] = useState([]);
  const [cust, setCust] = useState({ name: "", phone: "" });
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [done, setDone] = useState(null);
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["kiosk-menu", slug],
    queryFn: async () => (await api.get(guestApi(slug, "/restaurant"))).data,
    enabled: !!slug,
  });

  const cats = data?.categories || [];
  const sym = data?.currency_symbol || "₹";
  const filtered = useMemo(() => (data?.items || []).filter((m) => {
    if (m.available === false) return false;
    if (cat !== "all" && m.category_id !== cat) return false;
    if (search && !m.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [data?.items, cat, search]);

  const add = (m) => {
    const price = itemPrice(m, mode);
    setCart((c) => {
      const ex = c.find((x) => x.menu_item_id === m.id);
      if (ex) return c.map((x) => (x.menu_item_id === m.id ? { ...x, qty: x.qty + 1 } : x));
      return [...c, {
        menu_item_id: m.id,
        name: m.name,
        price,
        qty: 1,
        tax_rate: m.tax_rate ?? data?.default_tax_rate ?? 5,
        is_veg: m.is_veg,
      }];
    });
  };

  const chg = (id, d) => setCart((c) => c
    .map((x) => (x.menu_item_id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x))
    .filter((x) => x.qty > 0));

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const tax = cart.reduce((s, i) => s + i.price * i.qty * ((i.tax_rate || 5) / 100), 0);
  const total = subtotal + tax;

  const checkout = async (method) => {
    if (!cart.length) return toast.error("Select items");
    setBusy(true);
    try {
      const { data: order } = await api.post(guestApi(slug, "/kiosk/orders"), {
        items: cart,
        order_type: mode,
        customer_name: cust.name,
        customer_phone: cust.phone,
      });
      if (method === "upi") {
        const { data: upi } = await api.get(guestApi(slug, `/payments/upi/${order.id}`));
        setQr(await QRCode.toDataURL(upi.uri, { width: 280, margin: 1 }));
        setDone({ ...order, pay: "upi" });
      } else {
        await api.post(guestApi(slug, `/kiosk/orders/${order.id}/pay`), { method: "cash" });
        setDone({ ...order, pay: "counter" });
        setQr("");
      }
      setCart([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmUpi = async () => {
    if (!done) return;
    await api.post(guestApi(slug, `/kiosk/orders/${done.id}/pay`), { method: "upi" });
    setDone({ ...done, pay: "paid" });
    toast.success("Paid. Collect your token.");
  };

  useEffect(() => {
    if (!done || done.pay !== "upi" || !done.id) return undefined;
    const t = setInterval(async () => {
      try {
        const { data: st } = await api.get(guestApi(slug, `/payments/upi/${done.id}/status`));
        if (st.payment_status === "paid") {
          setDone((prev) => (prev ? { ...prev, pay: "paid" } : prev));
          toast.success("UPI payment confirmed");
        }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [done, slug]);

  if (!slug) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#111] text-white p-8 text-center">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Kiosk needs your restaurant link</h1>
          <p className="text-white/60 mt-2">Open <code className="text-white/80">/r/your-slug/kiosk</code> from Settings.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#111] text-white/70">
        <div className="flex items-center gap-3 text-lg">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--kul-primary)]" />
          Loading menu…
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#111] text-white p-8 text-center">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Couldn’t load kiosk</h1>
          <p className="text-white/60 mt-2 max-w-md">{formatApiError(error?.response?.data?.detail) || error?.message || "Try again"}</p>
          <Button onClick={() => refetch()} className="mt-4 bg-[var(--kul-primary)] text-white">Retry</Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#111] text-white grid place-items-center p-8" data-testid="kiosk-done">
        <div className="text-center max-w-md w-full">
          <div className="text-sm text-white/50 uppercase tracking-widest">Your token</div>
          <div className="font-display text-7xl font-extrabold text-[var(--kul-primary)] my-4">
            {done.token_number || done.order_number}
          </div>
          <p className="text-white/70">{done.order_number} · {money(done.total, sym)}</p>
          {qr && done.pay === "upi" && (
            <>
              <img src={qr} alt="UPI QR" className="mx-auto my-4 rounded-xl bg-white p-2" />
              <Button onClick={confirmUpi} className="bg-[var(--kul-primary)] text-white h-12 w-full">
                Waiting for UPI… or tap if paid
              </Button>
            </>
          )}
          {done.pay === "paid" && <p className="mt-4 text-emerald-400 font-medium">Payment confirmed</p>}
          {done.pay !== "upi" && (
            <p className="mt-4 text-white/60">Watch the token board. We’ll call your number when ready.</p>
          )}
          <Button
            variant="outline"
            className="mt-6 border-white/30 bg-white text-neutral-900 hover:bg-white/90"
            onClick={() => { setDone(null); setQr(""); }}
          >
            New order
          </Button>
        </div>
      </div>
    );
  }

  const cartPanel = (
    <div className="flex flex-col h-full min-h-0 bg-[#1c1c1e]">
      <div className="p-4 flex items-center justify-between border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2 font-display font-bold text-lg">
          <ShoppingBag className="w-5 h-5 text-[var(--kul-primary)]" />
          Your order
          {cartCount > 0 && (
            <span className="text-sm font-sans font-medium bg-[var(--kul-primary)] text-white px-2 py-0.5 rounded-md">
              {cartCount}
            </span>
          )}
        </div>
        {cart.length > 0 && (
          <button type="button" onClick={() => setCart([])} className="text-xs text-white/50 hover:text-white">
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {!cart.length && (
          <div className="h-full min-h-[120px] grid place-items-center text-center text-white/40 text-sm px-4">
            Tap dishes on the left to build your order
          </div>
        )}
        {cart.map((it) => (
          <div key={it.menu_item_id} className="flex items-center gap-3 rounded-xl bg-black/30 p-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {it.is_veg ? <Leaf className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <Beef className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                <span className="truncate font-medium">{it.name}</span>
              </div>
              <div className="text-sm text-white/50 mt-0.5">{money(it.price * it.qty, sym)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => chg(it.menu_item_id, -1)}
                className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center hover:bg-white/20"
                aria-label="Decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-6 text-center font-semibold">{it.qty}</span>
              <button
                type="button"
                onClick={() => chg(it.menu_item_id, 1)}
                className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center hover:bg-white/20"
                aria-label="Increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 space-y-3 border-t border-white/10 shrink-0">
        <div className="grid grid-cols-2 gap-2">
          <Input
            placeholder="Your name"
            value={cust.name}
            onChange={(e) => setCust({ ...cust, name: e.target.value })}
            className="h-11 bg-black/40 text-white border-white/20 placeholder:text-white/40"
          />
          <Input
            placeholder="Phone"
            value={cust.phone}
            onChange={(e) => setCust({ ...cust, phone: e.target.value })}
            className="h-11 bg-black/40 text-white border-white/20 placeholder:text-white/40"
          />
        </div>
        <div className="space-y-1 text-sm text-white/60">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal, sym)}</span></div>
          <div className="flex justify-between"><span>Tax</span><span>{money(tax, sym)}</span></div>
          <div className="flex justify-between font-display text-2xl font-extrabold text-white pt-1">
            <span>Total</span>
            <span className="text-[var(--kul-primary)]">{money(total, sym)}</span>
          </div>
        </div>
        <Button
          disabled={busy || !cart.length}
          onClick={() => checkout("upi")}
          className="w-full h-12 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white text-base font-semibold"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : `Pay UPI · ${money(total, sym)}`}
        </Button>
        <Button
          disabled={busy || !cart.length}
          variant="outline"
          onClick={() => checkout("cash")}
          className="w-full h-11 border-white/30 bg-white text-neutral-900 hover:bg-white/90 font-semibold"
        >
          Pay at counter
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-[100dvh] flex flex-col lg:flex-row bg-[#111] text-white overflow-hidden" data-testid="kiosk">
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Monitor className="w-6 h-6 text-[var(--kul-primary)] shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-xl sm:text-2xl font-extrabold truncate">
                {data?.restaurant_name || "ojent"} Kiosk
              </div>
              {data?.hours && <div className="text-xs text-white/45 truncate">{data.hours}</div>}
            </div>
          </div>
          <div className="flex gap-2">
            {["takeaway", "dine-in"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`px-4 py-2.5 rounded-lg capitalize text-sm font-semibold transition-colors ${
                  mode === m ? "bg-[var(--kul-primary)] text-white" : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
              >
                {m.replace("-", " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 pt-3 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search menu…"
              className="h-11 pl-10 bg-black/40 text-white border-white/15 placeholder:text-white/40"
            />
          </div>
        </div>

        <div className="flex gap-2 p-4 overflow-x-auto shrink-0">
          <button
            type="button"
            onClick={() => setCat("all")}
            className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
              cat === "all" ? "bg-[var(--kul-primary)]" : "bg-white/10 hover:bg-white/15"
            }`}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCat(c.id)}
              className={`px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                cat === c.id ? "bg-[var(--kul-primary)]" : "bg-white/10 hover:bg-white/15"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 pt-0 min-h-0">
          {!filtered.length ? (
            <div className="h-full min-h-[200px] grid place-items-center text-center text-white/45 px-6">
              <div>
                <UtensilsCrossed className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium text-white/70">No dishes to show</p>
                <p className="text-sm mt-1">
                  {(data?.items || []).length === 0
                    ? "Add available menu items in Menu Management."
                    : "Try another category or clear search."}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 pb-4 lg:pb-4">
              {filtered.map((m) => {
                const price = itemPrice(m, mode);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => add(m)}
                    className="rounded-2xl overflow-hidden bg-[#1c1c1e] text-left border border-white/5 hover:border-[var(--kul-primary)]/50 active:scale-[0.98] transition-all"
                  >
                    <div className="h-28 bg-neutral-800 relative">
                      {m.image ? (
                        <img src={asset(m.image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-white/25">
                          <UtensilsCrossed className="w-8 h-8" />
                        </div>
                      )}
                      <span className="absolute top-2 left-2 rounded-md bg-black/55 p-1">
                        {m.is_veg ? <Leaf className="w-3.5 h-3.5 text-emerald-400" /> : <Beef className="w-3.5 h-3.5 text-rose-400" />}
                      </span>
                    </div>
                    <div className="p-3">
                      <div className="font-medium line-clamp-2 leading-snug">{m.name}</div>
                      {m.description ? (
                        <div className="text-xs text-white/45 line-clamp-1 mt-1">{m.description}</div>
                      ) : null}
                      <div className="text-[var(--kul-primary)] font-semibold mt-2">{money(price, sym)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Desktop / large tablet cart */}
      <div className="hidden lg:flex w-[400px] shrink-0 border-l border-white/10 flex-col min-h-0">
        {cartPanel}
      </div>

      {/* Mobile / small tablet cart — sticky bottom sheet */}
      <div className="lg:hidden border-t border-white/10 max-h-[42vh] flex flex-col shrink-0">
        {cartPanel}
      </div>
    </div>
  );
}
